import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText,
  DialogTitle, Divider, Grid, Paper, Table, TableBody, TableCell, TableHead, TableRow,
  Typography, Alert, Stack, Tooltip,
} from '@mui/material';
import {
  ArrowBack, Assignment, Business, ReceiptLong, Inventory2, Cancel as CancelIcon,
  Timeline as TimelineIcon, CheckCircle, Article, Warehouse as WarehouseIcon, LocalShipping,
} from '@mui/icons-material';
import { scmService, GoodsReceiptDetail, GrnStatus } from '../../services/scmService';
import { StatusDot } from '../../components/common/pageKit';
import AttachmentsPanel from '../../components/common/AttachmentsPanel';

const inr = (n: any) => `₹${new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0)}`;
const fmtDate = (d: any) => (d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const num = (v: any) => Number(v) || 0;

const STATUS_TONE: Record<GrnStatus, any> = {
  DRAFT: 'neutral', RECEIVED: 'info', QC_PENDING: 'warning', ACCEPTED: 'success', REJECTED: 'error', CANCELLED: 'neutral',
};

const SectionCard: React.FC<{ title: string; icon: React.ReactNode; action?: React.ReactNode; children: React.ReactNode }> = ({ title, icon, action, children }) => (
  <Paper variant="outlined" sx={{ borderRadius: 2, mb: 2 }}>
    <Box sx={{ px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', gap: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
      {icon}
      <Typography sx={{ fontWeight: 700, fontSize: 14 }}>{title}</Typography>
      <Box sx={{ flexGrow: 1 }} />
      {action}
    </Box>
    <Box sx={{ p: 2.5 }}>{children}</Box>
  </Paper>
);

const KV: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Grid size={{ xs: 6, sm: 4, md: 4 }}>
    <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
    <Typography variant="body2" sx={{ fontWeight: 600 }}>{value}</Typography>
  </Grid>
);

const GoodsReceiptDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [grn, setGrn] = useState<GoodsReceiptDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const { goodsReceipt } = await scmService.getGoodsReceiptById(id);
      setGrn(goodsReceipt);
      setError(null);
    } catch (e: any) {
      setError(e.message || 'Failed to load goods receipt');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const doCancel = async () => {
    if (!id) return;
    setBusy(true);
    try { await scmService.cancelGoodsReceipt(id); setConfirmCancel(false); await load(); }
    catch (e: any) { setError(e.message || 'Cancel failed'); }
    finally { setBusy(false); }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress /></Box>;
  if (error && !grn) return <Box sx={{ p: 3 }}><Alert severity="error" action={<Button onClick={load}>Retry</Button>}>{error}</Alert></Box>;
  if (!grn) return null;

  const items = grn.items || [];
  const totalReceived = items.reduce((s, it) => s + num(it.received_quantity), 0);
  const totalAccepted = items.reduce((s, it) => s + num(it.accepted_quantity), 0);
  const totalRejected = items.reduce((s, it) => s + num(it.rejected_quantity), 0);
  const totalValue = items.reduce((s, it) => s + num(it.received_quantity) * num(it.unit_price), 0);
  const cancellable = grn.status !== 'CANCELLED' && grn.status !== 'REJECTED';
  const inspected = grn.status === 'ACCEPTED' || grn.status === 'REJECTED';

  return (
    <Box sx={{ p: 3 }}>
      <Button size="small" startIcon={<ArrowBack />} onClick={() => navigate('/procurement/goods-receipts')} sx={{ mb: 1.5 }}>Goods Receipts</Button>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Header band */}
      <Paper variant="outlined" sx={{ borderRadius: 2, p: 2.5, mb: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
          <Box>
            <Typography sx={{ fontFamily: 'monospace', fontWeight: 800, fontSize: 20 }}>{grn.grn_number}</Typography>
            <Typography variant="body2" color="text.secondary">{grn.vendor_name} · against PO {grn.po_number}</Typography>
          </Box>
          <Box sx={{ flexGrow: 1 }} />
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="caption" color="text.secondary">Received Value</Typography>
            <Typography sx={{ fontWeight: 800, fontSize: 18 }}>{inr(totalValue)}</Typography>
          </Box>
          <Stack spacing={0.75} alignItems="flex-end">
            <StatusDot label={String(grn.status || '').replace(/_/g, ' ')} tone={STATUS_TONE[grn.status] || 'neutral'} />
            <Typography variant="caption" color="text.secondary">{fmtDate(grn.receipt_date)}</Typography>
          </Stack>
        </Box>
        <Divider sx={{ my: 1.5 }} />
        <Stack direction="row" spacing={1} flexWrap="wrap">
          {grn.po_id && <Button variant="outlined" size="small" startIcon={<Assignment />} onClick={() => navigate(`/procurement/orders/${grn.po_id}`)}>View PO {grn.po_number || ''}</Button>}
          {cancellable && (
            <Tooltip title="Reverses the stock movements booked by this receipt">
              <span><Button variant="text" color="error" size="small" startIcon={<CancelIcon />} disabled={busy} onClick={() => setConfirmCancel(true)}>Cancel GRN</Button></span>
            </Tooltip>
          )}
        </Stack>
      </Paper>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 7 }}>
          {/* Summary */}
          <SectionCard title="Summary" icon={<Business fontSize="small" color="primary" />}>
            <Grid container spacing={2}>
              <KV label="Vendor" value={`${grn.vendor_name}${grn.vendor_code ? ` (${grn.vendor_code})` : ''}`} />
              <KV label="Purchase Order" value={grn.po_number || '—'} />
              <KV label="Warehouse" value={grn.warehouse_name || '—'} />
              <KV label="Receipt Date" value={fmtDate(grn.receipt_date)} />
              <KV label="Vendor DC No." value={grn.vendor_dc_number || '—'} />
              <KV label="Vehicle No." value={grn.vehicle_number || '—'} />
              <KV label="E-Way Bill" value={grn.eway_bill_number || '—'} />
              <KV label="Remarks" value={grn.remarks || '—'} />
            </Grid>
          </SectionCard>

          {/* Line items */}
          <SectionCard title="Received Items" icon={<ReceiptLong fontSize="small" color="primary" />}>
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>{['Product', 'Batch', 'Ordered', 'Received', 'Accepted', 'Rejected', 'Rate', 'Value'].map((h) => (
                    <TableCell key={h} align={['Ordered', 'Received', 'Accepted', 'Rejected', 'Rate', 'Value'].includes(h) ? 'right' : 'left'} sx={{ fontWeight: 700, fontSize: 12 }}>{h}</TableCell>
                  ))}</TableRow>
                </TableHead>
                <TableBody>
                  {items.length === 0 && <TableRow><TableCell colSpan={8} align="center" sx={{ color: 'text.secondary', py: 3 }}>No line items on this receipt</TableCell></TableRow>}
                  {items.map((it) => (
                    <TableRow key={it.id}>
                      <TableCell>
                        <Typography
                          variant="body2"
                          onClick={it.product_id ? () => navigate(`/entity/material/${it.product_id}`) : undefined}
                          sx={{ fontWeight: 600, ...(it.product_id ? { color: 'primary.main', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } } : {}) }}
                        >
                          {it.product_name || `Product #${it.product_id}`}
                        </Typography>
                        {it.product_code && <Typography variant="caption" color="text.secondary">{it.product_code}</Typography>}
                      </TableCell>
                      <TableCell>
                        {it.batch_number ? (
                          <>
                            <Typography variant="body2">{it.batch_number}</Typography>
                            {it.expiry_date && <Typography variant="caption" color="text.secondary">Exp {fmtDate(it.expiry_date)}</Typography>}
                          </>
                        ) : '—'}
                      </TableCell>
                      <TableCell align="right">{num(it.ordered_quantity)} {it.unit_of_measure || ''}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>{num(it.received_quantity)}</TableCell>
                      <TableCell align="right" sx={{ color: 'success.main' }}>{num(it.accepted_quantity)}</TableCell>
                      <TableCell align="right" sx={{ color: num(it.rejected_quantity) > 0 ? 'error.main' : 'text.secondary' }}>{num(it.rejected_quantity)}</TableCell>
                      <TableCell align="right">{inr(it.unit_price)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>{inr(num(it.received_quantity) * num(it.unit_price))}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </SectionCard>
        </Grid>

        <Grid size={{ xs: 12, md: 5 }}>
          {/* Receipt totals */}
          <SectionCard title="Receipt Totals" icon={<Inventory2 fontSize="small" color="primary" />}>
            <Grid container spacing={1.5}>
              <KV label="Line Items" value={items.length} />
              <KV label="Qty Received" value={totalReceived} />
              <KV label="Qty Accepted" value={<Box component="span" sx={{ color: 'success.main' }}>{totalAccepted}</Box>} />
              <KV label="Qty Rejected" value={<Box component="span" sx={{ color: totalRejected > 0 ? 'error.main' : 'inherit' }}>{totalRejected}</Box>} />
              <KV label="Received Value" value={inr(totalValue)} />
            </Grid>
            {!inspected && grn.status !== 'CANCELLED' && (
              <Alert severity="info" sx={{ mt: 1.5 }}>
                Awaiting quality inspection. Accepted / rejected quantities update once QC is completed.
              </Alert>
            )}
          </SectionCard>

          {/* Logistics */}
          <SectionCard title="Logistics" icon={<LocalShipping fontSize="small" color="primary" />}>
            <Grid container spacing={1.5}>
              <KV label="DC Number" value={grn.vendor_dc_number || '—'} />
              <KV label="Vehicle" value={grn.vehicle_number || '—'} />
              <KV label="E-Way Bill" value={grn.eway_bill_number || '—'} />
              <KV label="Warehouse" value={<><WarehouseIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />{grn.warehouse_name || '—'}</>} />
            </Grid>
          </SectionCard>

          <Box sx={{ mb: 2 }}>
            <AttachmentsPanel entityType="goods_receipt" entityId={grn.id} title="Receipt Documents" />
          </Box>

          {/* Lifecycle */}
          <SectionCard title="Lifecycle" icon={<TimelineIcon fontSize="small" color="primary" />}>
            <Stack spacing={1.5}>
              <Stack direction="row" spacing={1} alignItems="center"><CheckCircle fontSize="small" color="success" /><Box><Typography variant="body2" sx={{ fontWeight: 600 }}>Received</Typography><Typography variant="caption" color="text.secondary">{fmtDate(grn.receipt_date)} · stock booked</Typography></Box></Stack>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                onClick={() => navigate(inspected ? '/manufacturing/quality/lots?sourceType=GRN' : '/manufacturing/quality/lots?tab=incoming')}
                sx={{ cursor: 'pointer', '&:hover': { opacity: 0.75 } }}
              >
                {inspected ? <CheckCircle fontSize="small" color={grn.status === 'REJECTED' ? 'error' : 'success'} /> : <Article fontSize="small" color="disabled" />}
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, textDecoration: 'underline', textDecorationColor: 'transparent', '&:hover': { textDecorationColor: 'inherit' } }}>Quality Inspection</Typography>
                  <Typography variant="caption" color="text.secondary">{inspected ? String(grn.status).replace(/_/g, ' ') : 'Pending — view in Quality'}</Typography>
                </Box>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">{grn.status === 'CANCELLED' ? <CancelIcon fontSize="small" color="error" /> : <Article fontSize="small" color="disabled" />}<Box><Typography variant="body2" sx={{ fontWeight: 600 }}>Cancellation</Typography><Typography variant="caption" color="text.secondary">{grn.status === 'CANCELLED' ? 'Receipt cancelled, stock reversed' : 'Active'}</Typography></Box></Stack>
            </Stack>
          </SectionCard>
        </Grid>
      </Grid>

      {/* Cancel confirm */}
      <Dialog open={confirmCancel} onClose={() => setConfirmCancel(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Cancel goods receipt {grn.grn_number}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This reverses the stock movements booked by this receipt and rolls back the received quantity on the linked purchase order. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmCancel(false)}>Keep Receipt</Button>
          <Button variant="contained" color="error" disabled={busy} onClick={doCancel}>Cancel GRN</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GoodsReceiptDetailPage;
